import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { Worker, WorkerPath } from '../../types';

interface WorkerState {
  workers: Record<string, Worker>;
  selectedWorkers: string[];
  workerPaths: Record<string, WorkerPath>;
  loading: boolean;
  error: string | null;
}

const initialState: WorkerState = {
  workers: {},
  selectedWorkers: [],
  workerPaths: {},
  loading: false,
  error: null,
};

const workersSlice = createSlice({
  name: 'workers',
  initialState,
  reducers: {
    setWorkers: (state, action: PayloadAction<Record<string, Worker>>) => {
      state.workers = action.payload;
      state.loading = false;
      state.error = null;
    },
    
    addWorker: (state, action: PayloadAction<Worker>) => {
      const worker = action.payload;
      state.workers[worker.employeeId] = worker;
    },
    
    updateWorker: (state, action: PayloadAction<Worker>) => {
      const worker = action.payload;
      if (state.workers[worker.employeeId]) {
        state.workers[worker.employeeId] = worker;
      }
    },
    
    removeWorker: (state, action: PayloadAction<string>) => {
      delete state.workers[action.payload];
    },
    
    selectWorker: (state, action: PayloadAction<string>) => {
      if (!state.selectedWorkers.includes(action.payload)) {
        state.selectedWorkers.push(action.payload);
      }
    },
    
    deselectWorker: (state, action: PayloadAction<string>) => {
      state.selectedWorkers = state.selectedWorkers.filter(id => id !== action.payload);
    },
    
    setSelectedWorkers: (state, action: PayloadAction<string[]>) => {
      state.selectedWorkers = action.payload;
    },
    
    clearSelectedWorkers: (state) => {
      state.selectedWorkers = [];
    },
    
    setWorkerPath: (state, action: PayloadAction<WorkerPath>) => {
      const path = action.payload;
      state.workerPaths[path.person_id] = path;
    },
    
    clearWorkerPaths: (state) => {
      state.workerPaths = {};
    },
    
    setLoading: (state, action: PayloadAction<boolean>) => {
      state.loading = action.payload;
    },
    
    setError: (state, action: PayloadAction<string | null>) => {
      state.error = action.payload;
      state.loading = false;
    },
  },
});

export const {
  setWorkers,
  addWorker,
  updateWorker,
  removeWorker,
  selectWorker,
  deselectWorker,
  setSelectedWorkers,
  clearSelectedWorkers,
  setWorkerPath,
  clearWorkerPaths,
  setLoading,
  setError,
} = workersSlice.actions;

export default workersSlice.reducer;
